import { Button } from '@mui/material';
import { GVTextButton } from 'components/lib';
import { useDispatch, useSelector } from 'react-redux';
import { getInspectionId, getInspectionSettings, getSelectedTool, inspection, app } from 'store';
import { startAutoPrepJob } from 'store/request/inspections/actions';
import { PDFManagerFactory } from 'pdftron';
import { AutoPrepJobStatus, DocumentTypes, PDFTronTools } from 'types';
import { useConfirmation } from 'utils';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { useAutoPrepStatusStateStore } from 'zstore/autoPrepStatusStateStore';
import selectTool from 'utils/selectTool/selectTool';
import { clearTextInputAnnotations } from 'utils/clearInputAnnotations/clearInputAnnotations';
import { TextSetting } from './TextSetting';
import styles from './QRDModeToggle.module.css';

export const QRDModeToggle = () => {
  const dispatch = useDispatch();
  const confirm = useConfirmation();
  const { qrdMode } = useSelector(getInspectionSettings);
  const inspectionId = useSelector(getInspectionId);
  const selectedTool = useSelector(getSelectedTool);
  const { setAutoPrepStatus } = useAutoPrepStatusStateStore();

  const handleSelection = (keepMarkups: boolean) => {
    if (keepMarkups) {
      return;
    } else {
      clearTextInputAnnotations();

      // after updating the input annotations we need to wait for the inspection to finish updating (auto-prep is started in updateInspection onSuccess)
      setAutoPrepStatus(AutoPrepJobStatus.pending);
    }
  };

  const renderDecisionButtons = (item: 'shape' | 'markups', handleSelection: (keep: boolean) => void) => {
    const KEEP_ITEM = true;
    return (
      <>
        <GVTextButton
          className={styles.action}
          id={`keep-${item}-button`}
          text={'Keep Markups'}
          onClick={() => {
            handleSelection(KEEP_ITEM);
            dispatch(app.actions.setCloseConfirm(true));
          }}
        />
        <Button
          id={`override-${item}-button`}
          onClick={() => {
            handleSelection(!KEEP_ITEM);
            dispatch(app.actions.setCloseConfirm(true));
          }}
          variant="contained"
          color="secondary"
        >
          Delete Markups
        </Button>
      </>
    );
  };

  const toggleQRDMode = () => {
    dispatch(inspection.actions.setInspectionSettings({ qrdMode: !qrdMode }));
  };

  const handleAutoPrep = () => {
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();

    const checkUnconfirmedGraphicZone = pdfDocManager?.checkUnconfirmedGraphicZones();
    const hasUnconfirmedZones = pdfDocManager?.hasUnconfirmedZones() || false;

    // check input annotations to see if one of the text markup tools has been used
    const usedTextTool = Object.values(DocumentTypes).some((docType) => {
      const inputAnnotations = PDFAnnotationManager?.getInputAnnotations(docType);
      if (inputAnnotations) {
        return inputAnnotations.some((annot) => {
          return (
            annot.usedTool === PDFTronTools.CROSSOUT ||
            annot.usedTool === PDFTronTools.ZONE ||
            annot.usedTool === PDFTronTools.MARQUEE_ZONE
          );
        });
      }
      return false;
    });

    if (checkUnconfirmedGraphicZone || hasUnconfirmedZones || usedTextTool) {
      // if a text markup tool was used or unconfirmed zones exist we warn the user that they will lose their markups by running auto-prep
      confirm({
        title: 'Delete markups?',
        variant: 'call-to-action',
        catchOnCancel: false,
        description: 'Running Auto-Prep will remove previous markups from the document. Would you like to continue?',
        acceptLabel: 'Delete Markups',
        additionalActions: renderDecisionButtons('markups', (keep) => {
          handleSelection(keep);
        }),
      });
    } else {
      // if we don't need to update the inspection input we start auto-prep directly
      dispatch(startAutoPrepJob(inspectionId));
    }
  };

  return (
    <TextSetting
      label="Match QRD / Brief to Artwork"
      data-testid="match_qrd_brief_to_artwork"
      checked={qrdMode || false}
      onChange={() => toggleQRDMode()}
      tooltipText="Upload your QRD Annex III document or any brief, along with corresponding artwork. Use the crop tool or layer controls to remove unnecessary elements. Annex III pages are detected automatically, or you can set a custom page range."
      preview={{
        show: qrdMode || false,
        onClick: handleAutoPrep,
      }}
    />
  );
};
